import { useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspace } from 'hooks/useWorkspace';

import HelpButton from 'Components/HelpButton';

import { getDesignReviewCountsAction,
  manageAuditAction, setDesignReviewHistory } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';
import { setUserProfile } from 'Redux/Actions/userActions';
import actionTypes from 'Redux/constants';

import { ROUTES } from 'routes/route.constants';

import { PLATFORM } from 'utlis/constants';
import { errorReactToastify, successReactToastify } from 'utlis/toasts';

import DashboardHeader from './components/DashboardHeader';
import EditAuditSidebar from './components/EditAuditSidebar';
import EmptyDashboardState from './components/EmptyDashboardState';
import LowCreditsAlert from './components/LowCreditsAlert';
import Overview from './components/Overview';
import ReviewsSection from './components/ReviewsSection';
import Sidebar from './components/Sidebar';

import styles from './styles.module.scss';

const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationObject = useLocation();
  const { selectedWorkspace, isLoadingWorkspaces } = useWorkspace();

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [isProductLibraryOpen, setIsProductLibraryOpen] = useState(false);

  const authUser = useSelector(state => state.userStore);
  const reviewCount = useSelector(state => state.designReview.reviewCount);
  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const designReviewCounts = useSelector(state => state.designReview.designReviewCounts);
  const tokenRefreshTimestamp = useSelector(state => state.userStore.tokenRefreshTimestamp);

  const [previousReviewsRemaining, setPreviousReviewsRemaining] = useState(null);
  const [showLowCreditsAlert, setShowLowCreditsAlert] = useState(false);
  const [isLoadingCounts, setIsLoadingCounts] = useState(true);

  // Edit sidebar state — lifted here so the sidebar can be rendered outside overflow-auto
  const [editingReview, setEditingReview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [restoringReviewUuid, setRestoringReviewUuid] = useState(null);

  // Increment these to signal ReviewsSection to refetch the corresponding list
  const [activeRefreshKey, setActiveRefreshKey] = useState(0);
  const [archivedRefreshKey, setArchivedRefreshKey] = useState(0);

  // Check if we're on a create or review detail route
  const reviewsBasePath = ROUTES.TEAM_REVIEWS_NEW.replace('/new', '');
  const isCreateReviewOpen = Boolean(
    locationObject.pathname === ROUTES.TEAM_REVIEWS_NEW
    || (locationObject.pathname.startsWith(reviewsBasePath) && locationObject.pathname !== ROUTES.TEAM_REVIEWS_NEW)
  );

  // Handle opening review for existing review - navigate to review URL
  const handleViewReview = review => {
    if (review && review.uuid) {
      dispatch(setCurrentRecord(review));
      navigate(ROUTES.TEAM_REVIEWS_DETAIL(review.uuid));
    }
  };

  // Handle opening new review - navigate to create URL
  const handleCreateNewReview = () => {
    navigate(ROUTES.TEAM_REVIEWS_NEW);
  };

  const checkAuthentication = async () => {
    try {
      const userObjectString = localStorage.getItem('userObject');

      if (!userObjectString) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

        navigate(loginUrl);

        return;
      }

      const userObject = JSON.parse(userObjectString);

      if (!userObject || !userObject.uuid) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

        navigate(loginUrl);

        return;
      }

      // Dispatch userObject to Redux to populate userStore
      dispatch({
        type: actionTypes.SIGN_IN_USER,
        payload: userObject,
      });
      setIsCheckingAuth(false);
    }
    catch (error) {
      // Handle invalid JSON in localStorage
      // eslint-disable-next-line no-console
      console.error('Error parsing userObject from localStorage:', error);
      localStorage.removeItem('userObject');

      const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

      navigate(loginUrl);
    }
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  const fetchDesignReviewCounts = async ({ showLoader = true } = {}) => {
    if (showLoader) {
      setIsLoadingCounts(true);
    }

    try {
      await dispatch(getDesignReviewCountsAction(PLATFORM, selectedWorkspace?.uuid));
    }
    finally {
      if (showLoader) {
        setIsLoadingCounts(false);
      }
    }
  };

  // Refetch counts on workspace change (once workspaces have loaded).
  useEffect(() => {
    if (isLoadingWorkspaces) {
      return;
    }

    fetchDesignReviewCounts();
  }, [dispatch, selectedWorkspace?.uuid, isLoadingWorkspaces]);

  // Refetch counts after a token refresh — silently, so it doesn't flash the layout loader.
  useEffect(() => {
    if (isLoadingWorkspaces || !tokenRefreshTimestamp) {
      return;
    }

    fetchDesignReviewCounts({ showLoader: false });
  }, [tokenRefreshTimestamp, isLoadingWorkspaces]);

  useEffect(() => {
    if (authUser && authUser.uuid) {
      dispatch(setUserProfile(authUser));
    }
  }, [authUser?.uuid, dispatch]);

  // Check localStorage for modal state on component mount (only after auth check)
  useEffect(() => {
    // Wait for auth check to complete
    if (isCheckingAuth) {
      return;
    }

    // Check if returning from Figma OAuth (backend redirects to /team/home?figmaConnected=True)
    const urlParams = new URLSearchParams(locationObject.search);
    const figmaConnected = urlParams.get('figmaConnected');
    const shouldOpenProductLibrary = localStorage.getItem('openProductLibrary') === 'true';

    if (figmaConnected === 'True' || figmaConnected === 'true') {
      // Refresh user profile to get updated Figma token after OAuth
      if (authUser && authUser.uuid) {
        dispatch(setUserProfile(authUser));
      }

      const oauthSource = localStorage.getItem('figma_oauth_source');

      // Route to the correct flow based on source, default to Product Library
      if (oauthSource === 'createReview') {
        handleCreateNewReview();
      }
      else {
        // Default: open Product Library (for 'productLibrary' source or no source)
        setIsProductLibraryOpen(true);
      }

      // Clear the localStorage flag after handling
      localStorage.removeItem('figma_oauth_source');

      // Clean up URL
      const newUrl = new URL(window.location.href);

      newUrl.searchParams.delete('figmaConnected');
      window.history.replaceState({}, '', newUrl);
    }
    else if (shouldOpenProductLibrary) {
      // Open product library when navigating from empty product library selection
      setIsProductLibraryOpen(true);
      localStorage.removeItem('openProductLibrary');
    }
  }, [isCheckingAuth, authUser, dispatch, handleCreateNewReview, locationObject.search]);

  // Check if user has no credits and show alert
  useEffect(() => {
    if (reviewCount && reviewCount.reviews_remaining !== undefined) {
      const currentRemaining = reviewCount.reviews_remaining;

      // Show alert if user has 0 credits, either:
      // 1. Just ran out (had credits before, now has 0)
      // 2. Already has 0 credits when loading the page (first time check)
      if (currentRemaining === 0) {
        if (
          // Case 1: Just ran out of credits
          (previousReviewsRemaining !== null && previousReviewsRemaining > 0)
          // Case 2: First time loading and already has 0 credits
          || (previousReviewsRemaining === null)
        ) {
          setShowLowCreditsAlert(true);
        }
      }

      // Update previous value for next comparison
      setPreviousReviewsRemaining(currentRemaining);
    }
  }, [reviewCount?.reviews_remaining, previousReviewsRemaining]);

  const handleShowLowCreditsAlert = () => {
    setShowLowCreditsAlert(true);
  };

  const handleCloseLowCreditsAlert = () => {
    setShowLowCreditsAlert(false);
  };

  // --- Edit sidebar handlers ---

  const handleEditReview = review => {
    setEditingReview(review);
  };

  const handleCloseSidebar = () => {
    setEditingReview(null);
  };

  const handleSaveTitle = async newTitle => {
    setIsSaving(true);

    try {
      await dispatch(manageAuditAction(editingReview.uuid, { title: newTitle }));

      // Immediately update the review title in Redux so the list reflects the change
      // without waiting for a refetch. review_frame_name is the field shown in the list.
      const updatedResults = designReviewHistory.results?.map(r => r.uuid === editingReview.uuid
        ? { ...r, extra_data: { ...r.extra_data, review_frame_name: newTitle } }
        : r
      );

      dispatch(setDesignReviewHistory({ ...designReviewHistory, results: updatedResults }));

      successReactToastify('Flow details successfully updated', { autoClose: 3000 });
      handleCloseSidebar();
    }
    catch {
      errorReactToastify('Failed to update flow title. Please try again.', { autoClose: 3000 });
    }
    finally {
      setIsSaving(false);
    }
  };

  const handleArchiveReview = async () => {
    setIsArchiving(true);

    const reviewTitle = editingReview.extra_data?.review_frame_name || 'Flow';

    try {
      await dispatch(manageAuditAction(editingReview.uuid, { is_archived: true }));
      successReactToastify(`${reviewTitle} has been archived`, { autoClose: 3000 });
      handleCloseSidebar();
      setActiveRefreshKey(k => k + 1);
      setArchivedRefreshKey(k => k + 1);
      dispatch(getDesignReviewCountsAction(PLATFORM, selectedWorkspace?.uuid));
    }
    catch {
      errorReactToastify('Failed to archive flow. Please try again.', { autoClose: 3000 });
    }
    finally {
      setIsArchiving(false);
    }
  };

  const handleRestoreReview = async review => {
    const reviewTitle = review.extra_data?.review_frame_name || 'Flow';

    setRestoringReviewUuid(review.uuid);

    try {
      await dispatch(manageAuditAction(review.uuid, { is_archived: false }));
      successReactToastify(`${reviewTitle} has been restored`, { autoClose: 3000 });
      setArchivedRefreshKey(k => k + 1);
      setActiveRefreshKey(k => k + 1);
      dispatch(getDesignReviewCountsAction(PLATFORM, selectedWorkspace?.uuid));
    }
    catch {
      errorReactToastify('Failed to restore flow. Please try again.', { autoClose: 3000 });
    }
    finally {
      setRestoringReviewUuid(null);
    }
  };

  const handleOpenProductLibrary = () => {
    setIsProductLibraryOpen(true);
  };

  // Determine if dashboard is completely empty (no reviews at all)
  const isDashboardEmpty = (
    designReviewCounts?.total_workspace_audits === 0
  );

  // Show loader while checking authentication
  if (isCheckingAuth) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner frontColor={'#28BDAE'} />
      </div>
    );
  }

  return (
    <div className={styles['dashboard-container']}>
      <div className="min-h-screen bg-gray-50 grid gap-0 flex w-full">
        <Sidebar />
        <div className="flex flex-1 bg-gray-50 p-2 pl-0 max-h-screen">
          {/*
           * This div is `position: relative` — the EditAuditSidebar renders
           * as an absolute child of this element, covering it fully.
           * It must NOT be inside the overflow-auto inner div below.
           */}
          <div className="flex flex-1 flex-col overflow-auto rounded-lg bg-white shadow-figma-sm">
            <DashboardHeader
              onShowLowCreditsAlert={handleShowLowCreditsAlert}
              isProductLibraryOpen={isProductLibraryOpen}
              setIsProductLibraryOpen={setIsProductLibraryOpen}
            />

            {isLoadingCounts
              ? (
                <div className="flex flex-1 justify-center items-center">
                  <ImpulseSpinner frontColor={'#28BDAE'} />
                </div>
              )
              : isDashboardEmpty
                ? (
                  <EmptyDashboardState
                    onCreateAudit={handleCreateNewReview}
                    onOpenProductLibrary={handleOpenProductLibrary}
                  />
                )
                : (
                  <div className="flex flex-1 overflow-auto flex-col px-6 pt-6 pb-12">
                    <Overview
                      onStatusFilter={setStatusFilter}
                      statusFilter={statusFilter}
                    />

                    <ReviewsSection
                      isCreateReviewOpen={isCreateReviewOpen}
                      statusFilter={statusFilter}
                      onClearStatusFilter={() => setStatusFilter(null)}
                      onViewReview={handleViewReview}
                      onCreateReviewToggle={handleCreateNewReview}
                      onEditReview={handleEditReview}
                      onRestoreReview={handleRestoreReview}
                      restoringReviewUuid={restoringReviewUuid}
                      activeRefreshKey={activeRefreshKey}
                      archivedRefreshKey={archivedRefreshKey}
                    />
                  </div>
                )}

            <HelpButton />

            {/*
             * EditAuditSidebar is rendered here — direct child of the `relative` white card,
             * NOT inside the overflow-auto div — so its absolute positioning covers the full card.
             */}
            {editingReview && (
              <EditAuditSidebar
                review={editingReview}
                onClose={handleCloseSidebar}
                onArchive={handleArchiveReview}
                onSave={handleSaveTitle}
                isSaving={isSaving}
                isArchiving={isArchiving}
              />
            )}
          </div>
        </div>
      </div>

      {/* Low Credits Alert */}
      {showLowCreditsAlert && (
        <LowCreditsAlert onClose={handleCloseLowCreditsAlert} />
      )}
    </div>
  );
};

export default DashboardHome;
